/** Formatting helpers. Metric units; Slovenian-style day-first dates in English ("24. Oct"). */
import { format, parseISO } from "date-fns";

const nf = (d: number) =>
  new Intl.NumberFormat("en-GB", { maximumFractionDigits: d, minimumFractionDigits: 0 });

export const DASH = "—";

export function num(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  return nf(digits).format(v);
}

export function km(v: number | null | undefined, digits = 0): string {
  return v === null || v === undefined ? DASH : `${num(v, digits)} km`;
}

export function meters(v: number | null | undefined): string {
  return v === null || v === undefined ? DASH : `${num(Math.round(v))} m`;
}

export function pct(v: number | null | undefined, digits = 0): string {
  return v === null || v === undefined ? DASH : `${num(v, digits)}%`;
}

/** 5400 → "1h30" ; 2700 → "45′" */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return DASH;
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}′`;
  return `${h}h${String(mm).padStart(2, "0")}`;
}

/** 1.5 → "1.5 h" */
export function hours(h: number | null | undefined, digits = 1): string {
  if (h === null || h === undefined) return DASH;
  return `${num(h, digits)} h`;
}

function toDate(d: string | Date): Date {
  return typeof d === "string" ? parseISO(d) : d;
}

/** "24. Oct" */
export function day(d: string | Date): string {
  return format(toDate(d), "d. MMM");
}
/** "Sat 24. Oct" */
export function dayLong(d: string | Date): string {
  return format(toDate(d), "EEE d. MMM");
}
/** "24. Oct 2026" */
export function dayYear(d: string | Date): string {
  return format(toDate(d), "d. MMM yyyy");
}
/** "07:30" */
export function time(d: string | Date): string {
  return format(toDate(d), "HH:mm");
}

export function isoDay(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/** The rider's "today": the server's (`/status`, the athlete's time zone) when known, else this device's local
 * date. Never the UTC date, which is yesterday until 01:00/02:00 in Slovenia (BR-038). */
export function athleteToday(serverToday?: string | null, now: Date = new Date()): string {
  return serverToday ?? isoDay(now);
}

export function relative(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "never";
  const diff = (now.getTime() - toDate(iso).getTime()) / 60000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.round(diff)} min ago`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)} h ago`;
  return day(iso);
}

export const SPORT_LABEL: Record<string, string> = {
  gravel: "Gravel",
  cycling: "Road",
  mtb: "MTB",
  indoor_cycling: "Indoor",
  hockey: "Hockey",
  running: "Run",
  hiking: "Hike",
  walking: "Walk",
  strength: "Strength",
  swimming: "Swim",
};
export const sportLabel = (s: string) =>
  SPORT_LABEL[s] ?? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export const KIND_LABEL: Record<string, string> = {
  rest: "Rest",
  endurance: "Endurance",
  long: "Long ride",
  back_to_back: "Back-to-back",
  recovery: "Recovery",
  tempo_climbs: "Tempo climbs",
  sweet_spot: "Sweet spot",
  ftp_test: "FTP test",
  hockey: "Hockey",
  sport: "Other sport",
  strength: "Strength",
  race: "Race",
  custom: "Custom",
};
export const kindLabel = (k: string) => KIND_LABEL[k] ?? k;

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** Freshness (TSB) in words, one set of cut-offs for every screen (BR-123). */
export function freshnessWord(tsb: number): { text: string; tone: "good" | "warn" | "bad" | "info" } {
  if (tsb > 15) return { text: "very fresh", tone: "info" };
  if (tsb > 5) return { text: "fresh", tone: "good" };
  if (tsb >= -10) return { text: "neutral", tone: "good" };
  if (tsb >= -25) return { text: "training load", tone: "warn" };
  return { text: "fatigued", tone: "bad" };
}

/** The rider's race as they named it, without a trailing year ("Atlas Mountain Race"): the same words on every
 * screen, never an abbreviation like "AMR" next to the full name (BR-134). */
export function goalName(goal: { name: string } | null | undefined): string {
  return goal?.name.replace(/ 20\d\d$/, "").trim() || "your race";
}
